/**
 * Objednávkové návrhy pod minimální zásobou.
 * Min / zásoba / objednávka = stejná MJ (ks / m) — bez nákupních balení.
 */

import {
  MATERIAL_PROCUREMENT_DEFAULT_CURRENCY,
  MATERIAL_PROCUREMENT_DECISION_RULE,
  type MaterialCheapestUnitPrice,
  type MaterialProcurementDecisionRule,
  type MaterialProcurementQuoteLineResult,
  type MaterialProcurementSupplierGroup,
} from '../constants/material-procurement.constants';
import { isMaterialStockBelowMinimum } from './material-min-stock.util';
import {
  parseMaterialUnit,
  roundMaterialUsageQuantity,
} from './material-quantity.util';

/** Klíč SAP × sklad. */
export function materialReplenishmentStockKey(
  sapCode: string,
  warehouseId: string,
): string {
  return `${String(sapCode ?? '').trim()}::${String(warehouseId ?? '').trim()}`;
}

/**
 * Nákupní množství, které pokryje deficit ve skladové jednotce.
 * Pack/balení zrušeno — identity (ks/m).
 */
export function purchaseQuantityToCoverUsageDeficit(
  deficitUsage: number,
  _catalogUnit?: string | null,
): number {
  if (!Number.isFinite(deficitUsage) || deficitUsage <= 0) return 0;
  return roundMaterialUsageQuantity(deficitUsage);
}

export interface MaterialReplenishmentMinRow {
  sapCode: string;
  warehouseId: string;
  minQuantity: number;
  warehouseName?: string | null;
}

export interface MaterialReplenishmentStockRow {
  sapCode: string;
  warehouseId: string;
  quantityAvailable: number;
  materialName?: string | null;
  /** Catalog `jednotka` (prefer) nebo ERP unit. */
  catalogUnit?: string | null;
}

export interface MaterialReplenishmentProposalLine {
  sapCode: string;
  materialName: string | null;
  warehouseId: string;
  warehouseName: string | null;
  quantityAvailable: number;
  minQuantity: number;
  /** min − available (usage). */
  deficitUsage: number;
  /** Množství k objednání v nákupní MJ. */
  orderQuantityPurchase: number;
  usageUnit: string;
  purchaseUnit: string;
  catalogUnit: string | null;
  supplierId: string | null;
  supplierName: string | null;
  unitPrice: number | null;
  currency: string | null;
  lineTotal: number | null;
  priceId: string | null;
  unresolved: boolean;
  reason?: string;
}

export interface MaterialReplenishmentProposalsResult {
  decisionRule: MaterialProcurementDecisionRule;
  asOf: string;
  lines: MaterialReplenishmentProposalLine[];
  bySupplier: MaterialProcurementSupplierGroup[];
  unresolvedCount: number;
  grandTotal: number;
  currency: string;
  /** Počet řádků pod minimem (včetně unresolved). */
  belowMinCount: number;
}

function roundMoney(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Sestaví návrhy: min × zásoba → deficit → nákupní qty + nejlevnější dodavatel.
 * `cheapestByLineIndex` musí odpovídat pořadí below-min řádků (stejné jako vstupní pořadí
 * po filtrování), nebo použij `cheapestBySap` (1 cena na SAP — méně přesné u MOQ).
 */
export function buildMaterialReplenishmentProposals(
  mins: readonly MaterialReplenishmentMinRow[],
  stockByKey: ReadonlyMap<string, MaterialReplenishmentStockRow>,
  opts: {
    asOf?: string;
    /** Preferované: quote výsledek per below-min řádek (index). */
    quoteLines?: readonly MaterialProcurementQuoteLineResult[];
    /** Fallback: nejlevnější cena per SAP (qty=1 / bez per-line MOQ). */
    cheapestBySap?: ReadonlyMap<string, MaterialCheapestUnitPrice>;
    currency?: string;
  } = {},
): MaterialReplenishmentProposalsResult {
  const asOf = (opts.asOf?.trim() || new Date().toISOString().slice(0, 10)).slice(0, 10);
  const currency = opts.currency || MATERIAL_PROCUREMENT_DEFAULT_CURRENCY;
  const lines: MaterialReplenishmentProposalLine[] = [];

  let quoteIdx = 0;
  for (const min of mins) {
    const sapCode = String(min.sapCode ?? '').trim();
    const warehouseId = String(min.warehouseId ?? '').trim();
    if (!sapCode || !warehouseId) continue;
    const minQuantity = Number(min.minQuantity);
    if (!Number.isFinite(minQuantity) || minQuantity <= 0) continue;

    const stock = stockByKey.get(materialReplenishmentStockKey(sapCode, warehouseId));
    const quantityAvailable = Number(stock?.quantityAvailable) || 0;
    if (!isMaterialStockBelowMinimum(quantityAvailable, minQuantity)) continue;

    const deficitUsage = roundMaterialUsageQuantity(minQuantity - quantityAvailable);
    const catalogUnit = stock?.catalogUnit ?? null;
    const unitInfo = parseMaterialUnit(catalogUnit);
    const orderQuantityPurchase = purchaseQuantityToCoverUsageDeficit(
      deficitUsage,
      catalogUnit,
    );

    const quoteLine = opts.quoteLines?.[quoteIdx];
    quoteIdx += 1;
    const cheapest = opts.cheapestBySap?.get(sapCode);

    let supplierId: string | null = null;
    let supplierName: string | null = null;
    let unitPrice: number | null = null;
    let lineCurrency: string | null = null;
    let lineTotal: number | null = null;
    let priceId: string | null = null;
    let unresolved = true;
    let reason: string | undefined = 'Chybí platná cena v ceníku';

    if (quoteLine) {
      supplierId = quoteLine.supplierId;
      supplierName = quoteLine.supplierName;
      unitPrice = quoteLine.unitPrice;
      lineCurrency = quoteLine.currency;
      lineTotal = quoteLine.lineTotal;
      priceId = quoteLine.priceId;
      unresolved = quoteLine.unresolved;
      reason = quoteLine.reason;
    } else if (cheapest) {
      supplierId = cheapest.supplierId;
      supplierName = cheapest.supplierName;
      unitPrice = cheapest.unitPrice;
      lineCurrency = cheapest.currency;
      priceId = cheapest.priceId;
      lineTotal =
        unitPrice != null && Number.isFinite(orderQuantityPurchase)
          ? roundMoney(unitPrice * orderQuantityPurchase)
          : null;
      unresolved = false;
      reason = undefined;
    }

    lines.push({
      sapCode,
      materialName: stock?.materialName ?? null,
      warehouseId,
      warehouseName: min.warehouseName ?? null,
      quantityAvailable,
      minQuantity,
      deficitUsage,
      orderQuantityPurchase,
      usageUnit: unitInfo.usageUnit,
      purchaseUnit: unitInfo.purchaseUnit,
      catalogUnit,
      supplierId,
      supplierName,
      unitPrice,
      currency: lineCurrency,
      lineTotal,
      priceId,
      unresolved,
      reason,
    });
  }

  const groupMap = new Map<string, MaterialProcurementSupplierGroup>();
  for (const line of lines) {
    if (line.unresolved || !line.supplierId) continue;
    let group = groupMap.get(line.supplierId);
    if (!group) {
      group = {
        supplierId: line.supplierId,
        supplierName: line.supplierName || '',
        currency: line.currency || currency,
        lineCount: 0,
        subtotal: 0,
        lines: [],
      };
      groupMap.set(line.supplierId, group);
    }
    group.lineCount += 1;
    group.subtotal = roundMoney(group.subtotal + (line.lineTotal ?? 0));
    group.lines.push({
      sapCode: line.sapCode,
      quantity: line.orderQuantityPurchase,
      supplierId: line.supplierId,
      supplierName: line.supplierName,
      unitPrice: line.unitPrice,
      currency: line.currency,
      lineTotal: line.lineTotal,
      priceId: line.priceId,
      unresolved: false,
    });
  }

  const bySupplier = [...groupMap.values()].sort((a, b) =>
    a.supplierName.localeCompare(b.supplierName, 'cs', { sensitivity: 'base' }),
  );
  const unresolvedCount = lines.filter((l) => l.unresolved).length;
  const grandTotal = roundMoney(
    lines.reduce((sum, l) => sum + (l.unresolved ? 0 : l.lineTotal ?? 0), 0),
  );

  return {
    decisionRule: MATERIAL_PROCUREMENT_DECISION_RULE,
    asOf,
    lines,
    bySupplier,
    unresolvedCount,
    grandTotal,
    currency,
    belowMinCount: lines.length,
  };
}

/** Textový digest pro e-mail / in-app (max. řádků v těle). */
export function formatMaterialReplenishmentDigestMessage(
  result: MaterialReplenishmentProposalsResult,
  opts?: { maxLines?: number },
): string {
  const max = opts?.maxLines ?? 40;
  if (result.belowMinCount === 0) {
    return 'Doplnění zásob: žádný materiál není pod nastaveným minimem.';
  }
  const head = `Doplnění zásob: ${result.belowMinCount} pod minimem (celkem ${result.grandTotal.toFixed(2)} ${result.currency}).`;
  const sample = result.lines.slice(0, max).map((l) => {
    const wh = l.warehouseName || l.warehouseId;
    const supplier = l.unresolved
      ? 'bez ceny'
      : `${l.supplierName ?? '?'} @ ${l.unitPrice ?? '?'} ${l.currency ?? ''}`;
    return `• ${l.sapCode} / ${wh}: ${l.quantityAvailable}/${l.minQuantity} → obj. ${l.orderQuantityPurchase} ${l.purchaseUnit} (${supplier})`;
  });
  const more =
    result.lines.length > max ? `\n… a dalších ${result.lines.length - max}` : '';
  return [head, ...sample].join('\n') + more;
}
